import { useAuth0 } from '@auth0/auth0-react';
import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { AblyProvider } from 'ably/react';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import { Fragment, ReactNode, useEffect } from 'react';

import { AuthError } from '../auth/AuthError';
import { DrawerAppWrapper } from '../cdl/Drawer/DrawerAppWrapper';
import { queries } from '../common/api/queryKeys/queries';
import { usePrefetchQueries } from '../common/hooks/usePrefetchQueries';
import { client as launchDarklyClient } from '../common/launchDarkly/launchDarklyClient';
import { AblyService } from '../common/services/AblyService';
import { SidebarProvider } from '../common/Sidebar/SidebarProvider';
import AppSettings from '../constants';
import { Toolbox } from '../dev-tools/components/Toolbox';
import { FreshchatScript } from '../freshchat/FreshchatScript';
import { AcceptTerms } from '../group/terms/AcceptTerms';
import { LoadingWithWobble } from '../LoadingWithWobble';
import { registerDrawerNavigationGuard } from '../navigation/navigationHistory';
import { ProductlaneScript } from '../productlane/ProductlaneScript';
import { SurvicateContextProvider } from '../survicate/SurvicateContextProvider';
import { SessionService } from '../user/session/SessionService';

const MaybeDarkly = (props: { children: ReactNode }) => {
    return launchDarklyClient ? (
        <LDProvider clientSideID={AppSettings.launchDarklyClientSideId} ldClient={launchDarklyClient!} {...props} />
    ) : (
        <Fragment {...props} />
    );
};

const MaybeAbly = (props: { children: ReactNode }) => {
    const ablyClient = AblyService.getClient();

    return ablyClient ? <AblyProvider client={ablyClient} {...props} /> : <Fragment {...props} />;
};

const AuthenticatedApp = () => {
    usePrefetchQueries();

    const auth0 = useAuth0();

    const roleQuery = useQuery({
        queryKey: queries.role.information.queryKey,
        queryFn: async () => {
            const accessToken = await auth0.getAccessTokenSilently();
            return SessionService.getInstance().loadRoleInformation(accessToken);
        },
    });

    useEffect(() => {
        if (auth0.error) {
            Sentry.captureException(auth0.error);
        } else if (roleQuery.isError) {
            Sentry.captureException(roleQuery.error);
        }
    }, [auth0.error, roleQuery.error, roleQuery.isError]);

    if (roleQuery.isPending) {
        return <LoadingWithWobble />;
    }

    if (roleQuery.isError || (!auth0.isAuthenticated && auth0.error)) {
        if (auth0.error) {
            return <AuthError />;
        }
        return <AuthError />;
    }

    return (
        <>
            <FreshchatScript />
            <ProductlaneScript />
            <MaybeDarkly>
                <MaybeAbly>
                    <SidebarProvider>
                        <DrawerAppWrapper registerNavigationGuard={registerDrawerNavigationGuard}>
                            <SurvicateContextProvider>
                                <Outlet />
                                <AcceptTerms />
                                <Toolbox />
                            </SurvicateContextProvider>
                        </DrawerAppWrapper>
                    </SidebarProvider>
                </MaybeAbly>
            </MaybeDarkly>
        </>
    );
};

export const Route = createFileRoute('/_app')({
    beforeLoad: ({ context, location }) => {
        if (!context.auth.isAuthenticated) {
            throw redirect({
                to: '/login',
                search: {
                    redirect: location.href,
                },
            });
        }
    },
    component: AuthenticatedApp,
    errorComponent: AuthError,
});
